/**
 * InlineChatPaymentGate — Payment card rendered as a special message
 * inside the Dignity chat thread. Compact card-style layout that feels
 * like part of the conversation, not a separate page.
 */

import { useState } from 'react';
import { CreditCard, Shield, Sparkles, MessageSquare, FileText, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';

interface InlineChatPaymentGateProps {
  onPaymentComplete: (token: string) => void;
}

export function InlineChatPaymentGate({ onPaymentComplete }: InlineChatPaymentGateProps) {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  const [isProcessing, setIsProcessing] = useState(false);

  const handleMockPayment = async () => {
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    onPaymentComplete('mock-session-token-' + Date.now());
  };

  const features = [
    { icon: Sparkles, text: 'Personalized AI analysis' },
    { icon: MessageSquare, text: '15 messages with Claude AI' },
    { icon: FileText, text: 'Real hospital pricing data' },
    { icon: DollarSign, text: 'Savings & negotiation tips' },
  ];

  return (
    <div className={`mx-auto max-w-sm rounded-2xl border-2 overflow-hidden ${
      ahpDark
        ? 'border-[rgba(197,165,90,0.3)] bg-[#0f1d2f]'
        : isAhp
        ? 'border-amber-300 bg-gradient-to-b from-amber-50 to-orange-50'
        : 'border-amber-500/30 bg-gradient-to-b from-amber-500/10 to-amber-500/5'
    }`}>
      {/* Features grid */}
      <div className="grid grid-cols-2 gap-2 p-4 pb-3">
        {features.map(({ icon: Icon, text }, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <Icon className={`w-3.5 h-3.5 shrink-0 ${isAhp ? 'text-amber-600' : 'text-amber-400'}`} />
            <span className="text-[11px] text-muted-foreground">{text}</span>
          </div>
        ))}
      </div>

      {/* Price + button */}
      <div className={`p-4 pt-3 border-t ${
        ahpDark ? 'border-[rgba(197,165,90,0.15)]' : isAhp ? 'border-amber-200' : 'border-amber-500/15'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className={`text-2xl font-bold ${
              ahpDark ? 'text-[#f0ebe3]' : isAhp ? 'text-gray-900' : 'text-foreground'
            }`}>$1.99</span>
            <span className="text-xs text-muted-foreground ml-1.5">one-time</span>
          </div>
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Shield className="w-3 h-3" />
            <span>Stripe</span>
          </div>
        </div>

        <Button
          onClick={handleMockPayment}
          disabled={isProcessing}
          className={`w-full py-2.5 ${
            isAhp
              ? 'bg-amber-600 hover:bg-amber-700 text-white'
              : 'bg-amber-500 hover:bg-amber-600 text-black'
          }`}
        >
          {isProcessing ? (
            <span className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
              Processing...
            </span>
          ) : (
            <>
              <CreditCard className="w-4 h-4 mr-2" />
              Pay & Start AI Analysis
            </>
          )}
        </Button>

        <p className="text-center text-[9px] text-muted-foreground mt-2">
          Not happy? Contact us for a full refund.
        </p>
      </div>
    </div>
  );
}
